import { useCallback, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import { DeleteConfirmationDialog } from 'src/shared/components/organisms/deleteConfirmationDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { deleteMemberAutogiroStatus } from 'src/shared/services/memberAutogiroStatus';
import { deleteMemberSwishStatus } from 'src/shared/services/memberSwishStatusService';
import { ArrowDropUp } from '@mui/icons-material';
import { Button, ClickAwayListener, Grid, Grow, MenuItem, MenuList, Paper, Popper, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useIsMounted } from 'src/shared/hooks/useIsMounted';
import { MemberPaymentMethodType } from 'src/shared/models/enums/memberPaymentMethodType.enum';
import { getPermission } from 'src/shared/functions/getSysProductPermissions';
import { PermissionEnum } from 'src/shared/models/enums/permissionEnums';
import { useGlobals } from 'src/shared/contexts/appContext';
import { deleteElectronicInvoiceAddress } from 'src/shared/services/electronicInvoiceAddressService';
import { ElectronicInvoiceAddress } from 'src/modules/invoice/models/electronicInvoiceAddress';
import { AutogiroSettingsModal } from './autogiroSettingsModal';
import { EInvoiceSettingsModal } from './eInvoiceSettingsModal';
import { AutogiroStatus, Member, StripeStatus, SwishStatus } from '../models/member';

type MemberPaymentMethodProps = {
  member: Member;
  refetchMemberCallback: () => void;
  otherContactMode?: boolean;
};

type PaymentMethodConfig = {
  type: MemberPaymentMethodType;
  status: StripeStatus | AutogiroStatus | ElectronicInvoiceAddress | SwishStatus;
  label: string;
  disabled: boolean;
  onAdd: () => void;
  onDelete: () => void;
  showDeleteModal: boolean;
  modal: React.ReactNode;
};

export const MemberPaymentMethod = (props: MemberPaymentMethodProps) => {
  const { member, refetchMemberCallback, otherContactMode } = props;
  const { t } = useTranslation();
  const isMounted = useIsMounted();
  const { currentUser } = useGlobals();

  const [activeModal, setActiveModal] = useState<MemberPaymentMethodType | null>(null);
  const [openPaymentMethodMenu, setOpenPaymentMethodMenu] = useState<boolean>(false);

  const anchorRef = useRef<HTMLButtonElement>(null);
  const hasMemberUpdatePermission = getPermission(currentUser, PermissionEnum.MemberUpdate);

  const closeModal = useCallback(() => {
    setActiveModal(null);
  }, []);

  const getDisabledTooltip = (type: MemberPaymentMethodType) => {
    if (type === MemberPaymentMethodType.eInvoice) return t('member:paymentMethodDisabledByAutogiro');
    return t('member:paymentMethodDisabledByEInvoice');
  };

  const handleClose = (event: Event | React.SyntheticEvent) => {
    if (anchorRef.current && anchorRef.current.contains(event.target as HTMLElement)) {
      return;
    }
    setOpenPaymentMethodMenu(false);
  };

  const handleToggle = () => {
    setOpenPaymentMethodMenu((prevOpen) => !prevOpen);
  };

  const handleCloseDeleteAutogiro = useCallback(
    // eslint-disable-next-line consistent-return
    async (id: number | undefined) => {
      ConsoleHelper.log('handleCloseDeleteAutogiro', id);
      if (!id) return closeModal();

      try {
        await deleteMemberAutogiroStatus(id);
        if (isMounted()) {
          toast.success(<>{t('member:autogiroIsDeleted')}</>);
          refetchMemberCallback();
          closeModal();
        }
      } catch (e) {
        ConsoleHelper.log(e);
      }
    },
    [closeModal, isMounted, refetchMemberCallback, t],
  );

  const handleCloseDeleteEInvoice = useCallback(
    // eslint-disable-next-line consistent-return
    async (id: number | undefined) => {
      ConsoleHelper.log('handleCloseDeleteEInvoice', id);
      if (!id) return closeModal();

      try {
        await deleteElectronicInvoiceAddress(id);
        if (isMounted()) {
          toast.success(<>{t('member:eInvoiceIsDeleted')}</>);
          refetchMemberCallback();
          closeModal();
        }
      } catch (e) {
        ConsoleHelper.log(e);
      }
    },
    [closeModal, isMounted, refetchMemberCallback, t],
  );

  // Tar bort Swish Återkommande som betalsätt — raderar endast swishStatus-raden (medgivandet lämnas orört).
  const handleCloseDeleteSwish = useCallback(
    // eslint-disable-next-line consistent-return
    async (id: number | undefined) => {
      ConsoleHelper.log('handleCloseDeleteSwish', id);
      if (!id) return closeModal();

      try {
        await deleteMemberSwishStatus(id);
        if (isMounted()) {
          toast.success(<>{t('member:swishIsDeleted')}</>);
          refetchMemberCallback();
          closeModal();
        }
      } catch (e) {
        ConsoleHelper.log(e);
      }
    },
    [closeModal, isMounted, refetchMemberCallback, t],
  );

  // const handleCloseDeleteStripe = useCallback(
  //   // eslint-disable-next-line consistent-return
  //   async (id: number | undefined) => {
  //     ConsoleHelper.log('handleCloseDeleteStripe', id);
  //     if (!id) return closeModal();

  //     try {
  //       await deleteMemberStripeStatus(id);
  //       if (isMounted()) {
  //         toast.success(<>{t('member:stripeIsDeleted')}</>);
  //         refetchMemberCallback();
  //         closeModal();
  //       }
  //     } catch (e) {
  //       ConsoleHelper.log(e);
  //     }
  //   },
  //   [closeModal, isMounted, refetchMemberCallback, t],
  // );

  // Betalmetoder
  const paymentMethods = useMemo<PaymentMethodConfig[]>(
    () => [
      // Swish Återkommande tas med ENBART när medlemmen har en aktiv swish-status. Då döljer filtreringen längre
      // ned de andra "lägg till"-alternativen (precis som autogiro), så man måste ta bort Swish först. Borttagning
      // raderar endast swishStatus-raden — medgivandet (consenten) lämnas orört, exakt som autogiro lämnar mandatet.
      ...(member.swishStatus
        ? [
            {
              type: MemberPaymentMethodType.swishRecurring,
              status: member.swishStatus,
              label: t('member:deleteSwish'),
              disabled: false,
              onAdd: () => undefined,
              onDelete: () => setActiveModal(MemberPaymentMethodType.swishRecurring),
              showDeleteModal: activeModal === MemberPaymentMethodType.swishRecurring,
              modal: (
                <DeleteConfirmationDialog
                  title={t('member:confirmDeleteSwish')}
                  value={member.swishStatus.swishStatus_id.toString()}
                  open={activeModal === MemberPaymentMethodType.swishRecurring}
                  onClose={(val) => {
                    if (!val || !val.id) {
                      closeModal();
                      return;
                    }
                    handleCloseDeleteSwish(Number(val.id));
                  }}
                />
              ),
            } as PaymentMethodConfig,
          ]
        : []),
      {
        type: MemberPaymentMethodType.autogiro,
        status: member.autogiroStatus,
        label: member.autogiroStatus ? t('member:deleteAutogiro') : t('member:addAutogiro'),
        disabled: !member.autogiroStatus && !!member.eInvoiceAddress,
        onAdd: () => setActiveModal(MemberPaymentMethodType.autogiro),
        onDelete: () => setActiveModal(MemberPaymentMethodType.autogiro),
        showDeleteModal: activeModal === MemberPaymentMethodType.autogiro,
        modal: member.autogiroStatus ? (
          <DeleteConfirmationDialog
            title={t('member:confirmDeleteAutogiro')}
            value={member.autogiroStatus.autogiroStatus_id.toString()}
            open={activeModal === MemberPaymentMethodType.autogiro}
            onClose={(val) => {
              if (!val || !val.id) {
                closeModal();
                return;
              }
              handleCloseDeleteAutogiro(Number(val.id));
            }}
          />
        ) : (
          <AutogiroSettingsModal
            openAutogiroSettingsModal={activeModal === MemberPaymentMethodType.autogiro}
            setOpenAutogiroSettingsModal={closeModal}
            member={member}
            refetchMemberCallback={refetchMemberCallback}
          />
        ),
      },
      {
        type: MemberPaymentMethodType.eInvoice,
        status: member.eInvoiceAddress,
        label: member.eInvoiceAddress ? t('member:deleteEInvoice') : t('member:addEInvoice'),
        disabled: !member.eInvoiceAddress && !!member.autogiroStatus,
        onAdd: () => setActiveModal(MemberPaymentMethodType.eInvoice),
        onDelete: () => setActiveModal(MemberPaymentMethodType.eInvoice),
        showDeleteModal: activeModal === MemberPaymentMethodType.eInvoice,
        modal: member.eInvoiceAddress ? (
          <DeleteConfirmationDialog
            title={t('member:confirmDeleteEInvoice')}
            value={member.eInvoiceAddress.eInvoiceAddress_id.toString()}
            open={activeModal === MemberPaymentMethodType.eInvoice}
            onClose={(val) => {
              if (!val || !val.id) {
                closeModal();
                return;
              }
              handleCloseDeleteEInvoice(Number(val.id));
            }}
          />
        ) : (
          <EInvoiceSettingsModal
            open={activeModal === MemberPaymentMethodType.eInvoice}
            onClose={closeModal}
            member={member}
            refetchMemberCallback={refetchMemberCallback}
          />
        ),
      },
      //   {
      //     type: MemberPaymentMethodType.stripe,
      //     status: member.stripeStatus,
      //     label: member.stripeStatus ? t('member:deleteStripe') : t('member:addStripe'),
      //     onAdd: () => setActiveModal(MemberPaymentMethodType.stripe),
      //     onDelete: () => setActiveModal(MemberPaymentMethodType.stripe),
      //     showDeleteModal: activeModal === MemberPaymentMethodType.stripe,
      //     modal: member.stripeStatus ? (
      //       <DeleteConfirmationDialog
      //         title={t('member:confirmDeleteStripe')}
      //         value={member.stripeStatus.stripeStatus_id.toString()}
      //         open={activeModal === MemberPaymentMethodType.stripe}
      //         onClose={(val) => {
      //           if (!val || !val.id) {
      //             closeModal();
      //             return;
      //           }
      //           handleCloseDeleteStripe(Number(val.id));
      //         }}
      //       />
      //     ) : (
      //       <StripeSettingsModal
      //         openStripeSettingsModal={activeModal === MemberPaymentMethodType.stripe}
      //         setOpenStripeSettingsModal={closeModal}
      //         member={member}
      //         refetchMemberCallback={refetchMemberCallback}
      //       />
      //     ),
      //   },
    ],
    [member, t, activeModal, closeModal, refetchMemberCallback, handleCloseDeleteAutogiro, handleCloseDeleteEInvoice, handleCloseDeleteSwish],
  );

  return (
    <>
      <Button
        disabled={!hasMemberUpdatePermission}
        sx={{ mt: 2 }}
        ref={anchorRef}
        id='composition-button'
        aria-controls={openPaymentMethodMenu ? 'composition-menu' : undefined}
        aria-expanded={openPaymentMethodMenu ? 'true' : undefined}
        aria-haspopup='true'
        onClick={handleToggle}
        variant='outlined'
        size='small'
        disableElevation
        endIcon={<ArrowDropUp />}
      >
        {t('member:paymentMethod')}
      </Button>
      <Popper
        open={openPaymentMethodMenu}
        anchorEl={anchorRef.current}
        placement='bottom'
        transition
        disablePortal
        nonce={undefined}
        sx={{ zIndex: 999 }}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MenuList className='section-inset' id='composition-menu' aria-labelledby='composition-button'>
                  {/* Visa endast aktiva metoder - om ingen är aktiv, visa alla alternativ */}
                  {paymentMethods
                    .filter((pm) => !otherContactMode || pm.type !== MemberPaymentMethodType.autogiro)
                    .filter((pm) => pm.status || paymentMethods.every((m) => !m.status))
                    .map((pm) => (
                      <Tooltip key={pm.type} title={pm.disabled ? getDisabledTooltip(pm.type) : ''} arrow>
                        <span>
                          <MenuItem disabled={pm.disabled} onClick={pm.status ? pm.onDelete : pm.onAdd}>
                            {pm.label}
                          </MenuItem>
                        </span>
                      </Tooltip>
                    ))}
                </MenuList>
              </ClickAwayListener>
            </Paper>
          </Grow>
        )}
      </Popper>
      {paymentMethods.map(
        (pm) =>
          pm.showDeleteModal && (
            <Grid item key={pm.type}>
              {pm.modal}
            </Grid>
          ),
      )}
    </>
  );
};
